import { compareStrings, standardizeStringForComparison } from "@certn/utils";
import { css } from "@emotion/css";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFilter, useLabel } from "react-aria";
import {
  ComboBoxProps,
  Collection,
  Header,
  Virtualizer,
  ListLayout,
  ListBoxSection,
} from "react-aria-components";

import { _ComboBox } from "./assets/_ComboBox";
import { _ListBox } from "./assets/_ListBox";
import { _ListBoxItem } from "./assets/_ListBoxItem";
import { Text } from "../../../foundations/typography/Text";
import { useRefOffsetWidthResize } from "../../../hooks/useRefOffsetWidthResize";
import { _Popover } from "../../_assets/_Popover";
import { _Text } from "../../_assets/_Text";
import { Divider } from "../../divider/Divider";
import { _Input } from "../_assets/_Input";
import { _InputWrapper } from "../_assets/_InputWrapper";
import {
  FieldErrorsProps,
  FieldErrors,
} from "../_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../_assets/input-wrapper/FormLabelRAC";

export const SEARCHABLE_INPUT_CUSTOM_KEY = "OTHER";

export interface SearchableInputItem {
  key: string;
  label: string;
}
export interface SearchableInputSection {
  key: string;
  label?: string;
  items: SearchableInputItem[];
  // Mostly used to create a hidden section so `SEARCHABLE_INPUT_CUSTOM_KEY` is a valid key
  isHidden?: boolean;
}

export interface SearchableInputProps
  extends Omit<
    ComboBoxProps<SearchableInputItem>,
    | "children"
    | "isRequired"
    | "onSelectionChange"
    | "allowsCustomValue"
    | "menuTrigger"
    | "onInputChange" // Callback uses key, value args instead
    | "onBlur" // This event isn't directly captured so it's just called with no args
    | "onFocus" // This event isn't directly captured so it's just called with no args
    | "items" // Requires a specific format
    | "selectedKey" // Don't allow numbers
    | "defaultFilter"
    | "defaultInputValue"
    | "defaultItems"
    | "defaultSelectedKey"
  > {
  label?: string;
  description?: string;
  placeholder?: string;
  /**
   * @note Only affects the label, the enforcement should come from the errors prop.
   */
  isRequired?: boolean;
  errors?: FieldErrorsProps["children"];
  items: SearchableInputSection[] | SearchableInputItem[];
  selectedKey: string | null;
  onSelectionChange?: {
    (key: string | typeof SEARCHABLE_INPUT_CUSTOM_KEY, value: string): unknown;
    (key: null, value: null): unknown;
  };
  /**
   * The default text for the textbox. Only used if selectedKey is initially `SEARCHABLE_INPUT_CUSTOM_KEY`, otherwise it defaults to the label of `selectedKey`.
   */
  defaultCustomValue?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  info?: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * The parent will use "{baseTestID}-[combobox | input]" and the items will use "{baseTestID}-item-{key}"
   */
  baseTestID: string;

  /**
   * The popover won't be shown until the input has this many characters.
   *
   * @note if `maxFilteredResultsToShowPopover` is set, both conditions must be met.
   */
  minInputCharactersToShowPopover?: number;
  /**
   * The popover won't be shown until the input narrows the items down to this number or less.
   *
   * @note if `minInputCharactersToShowPopover` is set, both conditions must be met.
   */
  maxFilteredResultsToShowPopover?: number;

  onInputChange?: (matchingKey: string | null, value: string) => any;
  onBlur?: () => void;
  onFocus?: () => void;
}

export function SearchableInput(props: SearchableInputProps) {
  const { labelProps, fieldProps } = useLabel(props);
  const ref = useRef<HTMLDivElement>(null);
  const { inputWidth } = useRefOffsetWidthResize(ref);
  const { contains } = useFilter({ sensitivity: "base" });
  const {
    label,
    description,
    placeholder = "",
    isRequired,
    errors,
    items,
    selectedKey,
    defaultCustomValue,
    isLoading,
    isDisabled = false,
    onSelectionChange,
    onInputChange,
    onBlur,
    onFocus,
    onFocusChange,
    onOpenChange,
    info,
    footer,
    baseTestID,

    minInputCharactersToShowPopover = 0,
    maxFilteredResultsToShowPopover = Infinity,

    ...comboBoxProps
  } = props;

  const [inputValue, setInputValue] = useState("");
  const trimmedInputValue = inputValue.trim();
  useEffect(() => {
    if (!isLoading && trimmedInputValue.length === 0) {
      setInputValue(
        (selectedKey === SEARCHABLE_INPUT_CUSTOM_KEY
          ? defaultCustomValue
          : selectedItem?.label) ?? ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const sections = useMemo(
    () => searchableInputItemsToSections(items),
    [items]
  );
  const selectedItem = useMemo(
    () => findInSearchableInputSections(selectedKey, sections),
    [selectedKey, sections]
  );
  const filteredSections = useMemo(
    () => [
      ...sections.map((section) => {
        const filteredItems = section.items.filter(({ label }) =>
          contains(
            standardizeStringForComparison(label),
            standardizeStringForComparison(trimmedInputValue)
          )
        );

        return {
          key: section.key,
          label: filteredItems.length === 0 ? undefined : section.label,
          items: filteredItems,
          isHidden: section.isHidden,
        } satisfies SearchableInputSection;
      }),
      {
        key: "OTHER-SECTION",
        label: "",
        isHidden: true,
        items: [
          {
            key: SEARCHABLE_INPUT_CUSTOM_KEY,
            label: "",
          },
        ],
      },
    ],
    [trimmedInputValue, sections, contains]
  );
  const totalFilteredItems = useMemo(() => {
    return filteredSections.reduce(
      (total, section) => total + section.items.length,
      -1 // To account for the custom item
    );
  }, [filteredSections]);
  const topItem = useMemo(
    () =>
      filteredSections.reduce<SearchableInputItem | null>(
        (topItem, section) => {
          if (section.items.length === 0) {
            return topItem;
          }
          if (topItem) {
            const match = section.items.find(({ label }) =>
              compareStrings(label, trimmedInputValue)
            );
            if (match && !compareStrings(topItem.label, trimmedInputValue)) {
              // If there's a match, it replaces the first item that was found
              return match;
            }
            return topItem;
          } else {
            return section.items[0];
          }
        },
        null
      ),
    [trimmedInputValue, filteredSections]
  );

  function handleSelectionChange(key: string | null) {
    if (key === selectedKey) {
      if (key !== SEARCHABLE_INPUT_CUSTOM_KEY) {
        const selectedItem = findInSearchableInputSections(key, sections);
        if (selectedItem) {
          setInputValue(selectedItem.label); // Turns "bimm" for example to "BIMM" when unfocussed
        }
      }
      return;
    }

    if (key === null) {
      setInputValue("");
      onSelectionChange?.(null, null);
      // onInputChange will have already been called
    } else if (key === SEARCHABLE_INPUT_CUSTOM_KEY) {
      onSelectionChange?.(SEARCHABLE_INPUT_CUSTOM_KEY, trimmedInputValue);
      // onInputChange will have already been called
    } else {
      const label = findInSearchableInputSections(key, sections)!.label;
      setInputValue(label);
      onInputChange?.(key, label); // Likely only called with something that partially matches the item
      onSelectionChange?.(key, label);
    }
  }

  function getKeyToSelectForInputValue(inputValue: string) {
    if (topItem && compareStrings(inputValue.trim(), topItem.label ?? "")) {
      return topItem.key;
    } else if (inputValue.trim().length === 0) {
      return null;
    } else {
      return SEARCHABLE_INPUT_CUSTOM_KEY;
    }
  }

  const shouldShowSuggestions =
    totalFilteredItems > 0 &&
    totalFilteredItems <= maxFilteredResultsToShowPopover &&
    trimmedInputValue.length >= minInputCharactersToShowPopover;

  return (
    <div className={styles.wrapper}>
      {label && (
        <FormLabelRAC isRequired={isRequired} info={info} {...labelProps}>
          {label}
        </FormLabelRAC>
      )}

      <_ComboBox
        {...comboBoxProps}
        {...fieldProps}
        allowsCustomValue
        menuTrigger="focus"
        items={filteredSections}
        isRequired={isRequired}
        isDisabled={isDisabled}
        data-testid={`${baseTestID}-combobox`}
        inputValue={inputValue}
        onInputChange={(newValue) => {
          setInputValue(newValue);

          const keyIfConfirmed = getKeyToSelectForInputValue(newValue);
          onInputChange?.(
            keyIfConfirmed,
            keyIfConfirmed === SEARCHABLE_INPUT_CUSTOM_KEY
              ? newValue.trim()
              : findInSearchableInputSections(keyIfConfirmed, sections)
                  ?.label ?? ""
          );
        }}
        selectedKey={selectedKey}
        onSelectionChange={(key) => {
          // key seems to be null when a custom value is edited, despite the types indicating this should never happen. Looks like it can just be ignored
          if (key !== null) {
            handleSelectionChange(key as string);
          }
        }}
        onFocusChange={(isFocused) => {
          if (!isFocused) {
            handleSelectionChange(getKeyToSelectForInputValue(inputValue));
          }

          onFocusChange?.(isFocused);
          if (isFocused) {
            onFocus?.();
          } else {
            onBlur?.();
          }
        }}
      >
        <div ref={ref}>
          <_InputWrapper>
            <_Input
              isLoading={isLoading}
              placeholder={placeholder}
              data-testid={`${baseTestID}-input`}
            />
          </_InputWrapper>
        </div>
        {description && (
          <_Text style="input/helper" color="text-weak" slot="description">
            {description}
          </_Text>
        )}
        <FieldErrors>{errors}</FieldErrors>
        {shouldShowSuggestions && ( // Otherwise an empty popover will show
          <_Popover
            offset={8}
            isLoading={isLoading || false}
            className={css({ width: `${inputWidth}px` })}
          >
            <Virtualizer
              layout={ListLayout}
              layoutOptions={{
                estimatedRowHeight: 40,
                gap: 4,
                padding: 4,
              }}
            >
              <_ListBox
                selectionMode="single"
                items={filteredSections.filter((section) => !section.isHidden)}
              >
                {(section) => (
                  <ListBoxSection id={section.key}>
                    {section.label && (
                      <Header className={styles.header}>
                        <Text color="text-weak" style="input/section-title">
                          {section.label}
                        </Text>
                      </Header>
                    )}
                    <Collection items={section.items}>
                      {(item) => (
                        <_ListBoxItem
                          id={item.key}
                          key={item.key}
                          data-testid={`${baseTestID}-item-${item.key}`}
                        >
                          {item.label}
                        </_ListBoxItem>
                      )}
                    </Collection>
                  </ListBoxSection>
                )}
              </_ListBox>
            </Virtualizer>
            {footer && <Divider />}
            {footer}
          </_Popover>
        )}
      </_ComboBox>
    </div>
  );
}

export function searchableInputItemsToSections(
  items: SearchableInputItem[] | SearchableInputSection[]
): SearchableInputSection[] {
  const item = items[0];
  if (!item) return [{ key: "LISTED-SECTION", items: [] }];

  return (items[0] as { items?: unknown } | undefined)?.items
    ? (items as SearchableInputSection[])
    : ([{ key: "LISTED-SECTION", items }] as SearchableInputSection[]);
}

export function findInSearchableInputSections(
  key: string | null,
  sections: SearchableInputSection[]
) {
  if (key == null) return null;

  for (const section of sections) {
    const item = section.items.find((item) => item.key === key);
    if (item) {
      return item;
    }
  }
  return null;
}

const styles = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  header: css({
    padding: "var(--space-2) var(--space-3)",
    display: "flex",
  }),
};
